import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sportscanner/ui";
import { clubFeaturesSchema } from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { Section, Subsection, Row } from "@/components/settings-form";

interface ClubFeatureData {
  id: string;
  name: string;
  collectPhone: boolean;
  requirePhone: boolean;
  requirePhoneOtp: boolean;
  sendBookingConfirmationSms: boolean;
  sendBookingReminderSms: boolean;
  collectEmail: boolean;
  requireEmail: boolean;
  requireEmailOtp: boolean;
  sendBookingConfirmationEmail: boolean;
  sendBookingReminderEmail: boolean;
  requireLogin: boolean;
  requireMembership: boolean;
  showPublicSchedule: boolean;
  resultsEnabled: boolean;
  collectBookingComment: boolean;
}

interface Props {
  clubId: string;
}

export default function AccessForm({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId, "features"],
    queryFn: async () => {
      const data = await apiGet<{ club: ClubFeatureData }>(
        `/api/admin/clubs/${clubId}`,
      );
      return data.club;
    },
  });
  const club = clubQuery.data;

  // ── Account & membership ───────────────────────────────────────────────
  const [requireLogin, setRequireLogin] = useState(false);
  const [requireMembership, setRequireMembership] = useState(false);
  const [showPublicSchedule, setShowPublicSchedule] = useState(false);
  const [resultsEnabled, setResultsEnabled] = useState(false);

  // ── Contact channels ────────────────────────────────────────────────────
  // OTP toggles + SMS confirmation are pinned off in `eff` below since the UI
  // for them is hidden; their state still exists so the form can reset
  // cleanly when a club arrives with stale-true values and surface them as a
  // "dirty" save that clears them.
  const [collectPhone, setCollectPhone] = useState(false);
  const [requirePhone, setRequirePhone] = useState(false);
  const [requirePhoneOtp, setRequirePhoneOtp] = useState(false);
  const [sendBookingConfirmationSms, setSendBookingConfirmationSms] =
    useState(false);
  const [sendBookingReminderSms, setSendBookingReminderSms] = useState(false);
  const [collectEmail, setCollectEmail] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [requireEmailOtp, setRequireEmailOtp] = useState(false);
  const [sendBookingConfirmationEmail, setSendBookingConfirmationEmail] =
    useState(false);
  const [sendBookingReminderEmail, setSendBookingReminderEmail] =
    useState(false);
  const [collectBookingComment, setCollectBookingComment] = useState(false);

  // Reset form when the loaded club changes (e.g. SUPERADMIN picks a different).
  useEffect(() => {
    if (!club) return;
    setRequireLogin(club.requireLogin);
    setRequireMembership(club.requireMembership);
    setShowPublicSchedule(club.showPublicSchedule);
    setResultsEnabled(club.resultsEnabled);

    setCollectPhone(club.collectPhone);
    setRequirePhone(club.requirePhone);
    setRequirePhoneOtp(club.requirePhoneOtp);
    setSendBookingConfirmationSms(club.sendBookingConfirmationSms);
    setSendBookingReminderSms(club.sendBookingReminderSms);
    setCollectEmail(club.collectEmail);
    setRequireEmail(club.requireEmail);
    setRequireEmailOtp(club.requireEmailOtp);
    setSendBookingConfirmationEmail(club.sendBookingConfirmationEmail);
    setSendBookingReminderEmail(club.sendBookingReminderEmail);
    setCollectBookingComment(club.collectBookingComment);

    setErrorCode(null);
  }, [club]);

  // Effective values — mirror server normalisation so the form never lies
  // about what's actually persisted. OTP collapses when the channel isn't
  // required; confirmation messages need a contact source (the channel itself
  // OR a logged-in account).
  const eff = useMemo(() => {
    const eRequireLogin = requireLogin || requireMembership;
    // `require*` implies `collect*` — a required channel is always shown. When
    // login is required the verified account is the contact source, so the
    // per-booking phone/email channels are hidden (see the Contact section) and
    // pinned off here, keeping the saved state honest about what the booking
    // forms will actually ask for.
    const eCollectPhone = eRequireLogin ? false : collectPhone || requirePhone;
    const eRequirePhone = eRequireLogin ? false : requirePhone;
    const eCollectEmail = eRequireLogin ? false : collectEmail || requireEmail;
    const eRequireEmail = eRequireLogin ? false : requireEmail;
    // OTP + SMS confirmations + SMS reminders are hidden from the UI: OTP is
    // unfinished, SMS sending is ENTERPRISE-only and needs a per-tenant
    // provider key that isn't wired up yet. Pin them all off on save so a club
    // that had any of them on pre-launch gets them cleared on the next write.
    const ePhoneOtp = false;
    const eSendSms = false;
    const eSendReminderSms = false;
    const eEmailOtp = false;
    // Confirmation/reminder emails need a contact source: the email channel is
    // at least collected, or a logged-in account carries the address.
    const eSendEmail =
      (eCollectEmail || eRequireLogin) && sendBookingConfirmationEmail;
    const eSendReminderEmail =
      (eCollectEmail || eRequireLogin) && sendBookingReminderEmail;
    return {
      collectPhone: eCollectPhone,
      requirePhone: eRequirePhone,
      requirePhoneOtp: ePhoneOtp,
      sendBookingConfirmationSms: eSendSms,
      sendBookingReminderSms: eSendReminderSms,
      collectEmail: eCollectEmail,
      requireEmail: eRequireEmail,
      requireEmailOtp: eEmailOtp,
      sendBookingConfirmationEmail: eSendEmail,
      sendBookingReminderEmail: eSendReminderEmail,
      requireLogin: eRequireLogin,
      requireMembership,
      showPublicSchedule,
      resultsEnabled,
      collectBookingComment,
    };
  }, [
    collectPhone,
    requirePhone,
    collectEmail,
    requireEmail,
    sendBookingConfirmationEmail,
    sendBookingReminderEmail,
    requireLogin,
    requireMembership,
    showPublicSchedule,
    resultsEnabled,
    collectBookingComment,
  ]);

  const isDirty = useMemo(() => {
    if (!club) return false;
    return (
      eff.collectPhone !== club.collectPhone ||
      eff.requirePhone !== club.requirePhone ||
      eff.requirePhoneOtp !== club.requirePhoneOtp ||
      eff.sendBookingConfirmationSms !== club.sendBookingConfirmationSms ||
      eff.sendBookingReminderSms !== club.sendBookingReminderSms ||
      eff.collectEmail !== club.collectEmail ||
      eff.requireEmail !== club.requireEmail ||
      eff.requireEmailOtp !== club.requireEmailOtp ||
      eff.sendBookingConfirmationEmail !== club.sendBookingConfirmationEmail ||
      eff.sendBookingReminderEmail !== club.sendBookingReminderEmail ||
      eff.requireLogin !== club.requireLogin ||
      eff.requireMembership !== club.requireMembership ||
      eff.showPublicSchedule !== club.showPublicSchedule ||
      eff.resultsEnabled !== club.resultsEnabled ||
      eff.collectBookingComment !== club.collectBookingComment
    );
  }, [club, eff]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!club) throw new Error("no club");
      const parsed = clubFeaturesSchema.safeParse({
        collectPhone: eff.collectPhone,
        requirePhone: eff.requirePhone,
        requirePhoneOtp: eff.requirePhoneOtp,
        sendBookingConfirmationSms: eff.sendBookingConfirmationSms,
        sendBookingReminderSms: eff.sendBookingReminderSms,
        collectEmail: eff.collectEmail,
        requireEmail: eff.requireEmail,
        requireEmailOtp: eff.requireEmailOtp,
        sendBookingConfirmationEmail: eff.sendBookingConfirmationEmail,
        sendBookingReminderEmail: eff.sendBookingReminderEmail,
        requireLogin: eff.requireLogin,
        requireMembership: eff.requireMembership,
        showPublicSchedule: eff.showPublicSchedule,
        resultsEnabled: eff.resultsEnabled,
        collectBookingComment: eff.collectBookingComment,
      });
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut(`/api/admin/clubs/${club.id}/features`, parsed.data);
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("access.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId],
      });
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  const channelOptionLabels = {
    off: t("access.contact.off"),
    optional: t("access.contact.optional"),
    required: t("access.contact.required"),
  };

  if (clubQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (clubQuery.isError || !club) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      {/* Account & membership */}
      <Section title={t("access.sections.account")}>
        <Row
          id="ff-require-login"
          label={t("clubs.featuresDialog.requireLogin.label")}
          hint={t("clubs.featuresDialog.requireLogin.hint")}
          checked={eff.requireLogin}
          onCheckedChange={(v) => {
            setRequireLogin(v);
            if (!v) setRequireMembership(false);
          }}
          disabled={mutation.isPending || requireMembership}
        />
        {eff.requireLogin && (
          <div className="pl-4 border-l-2 border-neutral-100 space-y-4">
            <Row
              id="ff-require-membership"
              label={t("clubs.featuresDialog.requireMembership.label")}
              hint={t("clubs.featuresDialog.requireMembership.hint")}
              checked={requireMembership}
              onCheckedChange={setRequireMembership}
              disabled={mutation.isPending}
            />
            <Row
              id="ff-send-email-account"
              label={t(
                "clubs.featuresDialog.sendBookingConfirmationEmail.label",
              )}
              hint={t("clubs.featuresDialog.sendBookingConfirmationEmail.hint")}
              checked={sendBookingConfirmationEmail}
              onCheckedChange={setSendBookingConfirmationEmail}
              disabled={mutation.isPending}
            />
            <Row
              id="ff-send-reminder-email-account"
              label={t("clubs.featuresDialog.sendBookingReminderEmail.label")}
              hint={t("clubs.featuresDialog.sendBookingReminderEmail.hint")}
              checked={sendBookingReminderEmail}
              onCheckedChange={setSendBookingReminderEmail}
              disabled={mutation.isPending}
            />
          </div>
        )}
        <Row
          id="ff-show-public-schedule"
          label={t("clubs.featuresDialog.showPublicSchedule.label")}
          hint={t("clubs.featuresDialog.showPublicSchedule.hint")}
          checked={showPublicSchedule}
          onCheckedChange={setShowPublicSchedule}
          disabled={mutation.isPending}
        />
        <Row
          id="ff-results-enabled"
          label={t("clubs.featuresDialog.resultsEnabled.label")}
          hint={t("clubs.featuresDialog.resultsEnabled.hint")}
          checked={resultsEnabled}
          onCheckedChange={setResultsEnabled}
          disabled={mutation.isPending}
        />
      </Section>

      {/* Contact channels */}
      <Section title={t("access.sections.contact")}>
        {eff.requireLogin && (
          <p className="text-xs text-neutral-500 leading-relaxed">
            {t("clubs.featuresDialog.requireLoginContactNote")}
          </p>
        )}

        {!eff.requireLogin && (
          <Subsection title={t("clubs.featuresDialog.phoneSection")}>
            <ChannelRow
              id="ff-phone-channel"
              label={t("access.contact.phone")}
              hint={t("access.contact.phoneHint")}
              value={
                eff.requirePhone
                  ? "required"
                  : eff.collectPhone
                    ? "optional"
                    : "off"
              }
              onValueChange={(v) => {
                setRequirePhone(v === "required");
                setCollectPhone(v === "optional" || v === "required");
                if (v !== "required") setRequirePhoneOtp(false);
              }}
              optionLabels={channelOptionLabels}
              disabled={mutation.isPending}
            />
          </Subsection>
        )}

        {!eff.requireLogin && (
          <Subsection title={t("clubs.featuresDialog.emailSection")}>
            <ChannelRow
              id="ff-email-channel"
              label={t("access.contact.email")}
              hint={t("access.contact.emailHint")}
              value={
                eff.requireEmail
                  ? "required"
                  : eff.collectEmail
                    ? "optional"
                    : "off"
              }
              onValueChange={(v) => {
                setRequireEmail(v === "required");
                setCollectEmail(v === "optional" || v === "required");
                if (v !== "required") setRequireEmailOtp(false);
              }}
              optionLabels={channelOptionLabels}
              disabled={mutation.isPending}
            />
            {eff.collectEmail && (
              <>
                <Row
                  id="ff-send-email"
                  label={t(
                    "clubs.featuresDialog.sendBookingConfirmationEmail.label",
                  )}
                  hint={t(
                    "clubs.featuresDialog.sendBookingConfirmationEmail.hint",
                  )}
                  checked={sendBookingConfirmationEmail}
                  onCheckedChange={setSendBookingConfirmationEmail}
                  disabled={mutation.isPending}
                />
                <Row
                  id="ff-send-reminder-email"
                  label={t(
                    "clubs.featuresDialog.sendBookingReminderEmail.label",
                  )}
                  hint={t("clubs.featuresDialog.sendBookingReminderEmail.hint")}
                  checked={sendBookingReminderEmail}
                  onCheckedChange={setSendBookingReminderEmail}
                  disabled={mutation.isPending}
                />
              </>
            )}
          </Subsection>
        )}

        <Subsection title={t("clubs.featuresDialog.bookingFormSection")}>
          <Row
            id="ff-collect-booking-comment"
            label={t("clubs.featuresDialog.collectBookingComment.label")}
            hint={t("clubs.featuresDialog.collectBookingComment.hint")}
            checked={collectBookingComment}
            onCheckedChange={setCollectBookingComment}
            disabled={mutation.isPending}
          />
        </Subsection>
      </Section>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!isDirty || mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}

// A contact channel (phone / email) as an Off / Optional / Required dropdown.
// `value` is derived from the (collect, require) pair upstream; the parent maps
// the chosen string back to both flags.
function ChannelRow({
  id,
  label,
  hint,
  value,
  onValueChange,
  optionLabels,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onValueChange: (v: string) => void;
  optionLabels: { off: string; optional: string; required: string };
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <Label htmlFor={id} className="text-sm font-medium text-neutral-800">
          {label}
        </Label>
        <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>
      </div>
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger id={id} className="w-36 rounded-lg border-neutral-200">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="off">{optionLabels.off}</SelectItem>
          <SelectItem value="optional">{optionLabels.optional}</SelectItem>
          <SelectItem value="required">{optionLabels.required}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
